import { CROption, PatchData } from '@smart-builder/types';
import { queryXtiId, SBStorage } from '@smart-builder/util';
import { HTTPMethod } from './resource';

export type TranslatableString = string; // now translated in the back-end

export type ErrorPayload = {
  code?: number;
  info?: any;
  message?: string;
  onFailure?: () => void;
};

export const ResponseCodes = {
  notFound: 404,
  preconditionFailed: 412,
  sbRequiresPrompt: 496,
  sbLicenceNotAccepted: 497,
  sbInvalidHeadSlope: 498,
  sbUnauthorized: 499,
};

// translation is now handled on the back-end
export function resolveTranslation(message: TranslatableString): string {
  return message;
}

// This is local to the current JS context (i.e. current tab)
//    don't store in localStorage
// This is used to determine whether to show dialogs such
//    as "Your session has been lost" when the current session
//    expires, which we only want to do for tabs in which the
//    user has made a change (PATCH request)
let sentAnyPatch = false;
export function hasSentAnyPatch() {
  return sentAnyPatch;
}

const sessionTokenQueryParamName = 'session';

function querySessionToken() {
  const queryParams = new URLSearchParams(document.location.search);
  return queryParams.get(sessionTokenQueryParamName) || undefined;
}

export function getSessionToken() {
  const sessionToken = querySessionToken();
  if (sessionToken) {
    SBStorage.setItem(sessionTokenKey(), sessionToken);
    const url = new URL(location.href);
    url.searchParams.delete(sessionTokenQueryParamName);
    window.history.pushState(null, '', url.href);
    return sessionToken;
  } else {
    const { ssoParams } = extractSSOParams();
    if (ssoParams.size > 0) {
      // if we have any sso params, we remove our current session (if any)
      // so we can attempt to log in with the new one.
      SBStorage.removeItem(sessionTokenKey());
    }
  }
  return SBStorage.getItem(sessionTokenKey());
}

export type APIResult = {
  response: Response;
  patchData?: PatchData;
  existsInDatabase?: boolean;
  modified?: boolean;
  result?: any; // if returning a single object
  results?: any[]; // if returning an array
  populateOptions?: Record<string, CROption[]>; // if returning options for populating w/ results array
  blob?: any; // if returning binary data
  lockNum?: number;
  seqNumCur?: number;
  seqNumPersisted?: number;
};

export type ApiRequestRange = {
  start: number;
  finish: number;
};

export type APIParams = {
  lockNum?: number;
  seqNumFrom?: number;
  queryParams?: any;
  range?: ApiRequestRange;
  body?: any;
  requestType?: 'json' | 'form';
  reloadSnapshots?: boolean;
};

type SessionBody = {
  token: string;
  isImperial: boolean;
  formatSettings: CRFormatSettings;
  xtiId: number;
  translations: Record<string, string>;
};

function updateSessionConfig(session?: SessionBody) {
  const { isImperial, xtiId, formatSettings } = session || {};

  SB_CONFIG.isImperial = isImperial ?? SB_CONFIG.isImperial;
  SB_CONFIG.formatSettings = { ...SB_CONFIG.formatSettings, ...formatSettings };
  SB_CONFIG.xtiId = xtiId ?? SB_CONFIG.xtiId;
}

function formatLockNum(headers: Record<string, string>, lockNum?: number) {
  if (lockNum) {
    headers['If-Match'] = `"${lockNum}"`;
  }
}

function parseLockNum(response: Response): number {
  let eTag = response.headers.get('ETag');

  // eTag should be surrounded by double-quotes, but handle either case
  if (eTag && eTag.startsWith('"') && eTag.endsWith('"')) {
    eTag = eTag.substring(1, eTag.length - 1);
  }

  return +(eTag || 0);
}

function parseSeqNumPersisted(response: Response): number {
  return +(response.headers.get('SB-Seq-Persisted') || 0);
}

function parseSeqNumCur(response: Response): number {
  return +(response.headers.get('SB-Seq-Current') || 0);
}

function parseSnapshotNumber(response: Response): bigint {
  const snapshotNum = response.headers.get('SB-Snapshot');
  return BigInt(snapshotNum || 0);
}

function formatRange(headers: Record<string, string>, range?: ApiRequestRange) {
  if (range) {
    const { start, finish } = range;
    headers['Range'] = `items=${start}-${finish}`;
  }
}

// TODO Should use CustomEvents instead
type AuthExpiredFn = () => APIResult;
let onAuthenticationExpired: AuthExpiredFn = () => {
  console.warn('callApi.onAuthenticationExpired - not registered');
  return { response: {} as any };
};
export function registerOnAuthenticationExpired(handler: AuthExpiredFn) {
  onAuthenticationExpired = handler;
}

// TODO Should use CustomEvents instead
export type LicenceResponse = {
  canAccept: boolean;
  licence: string;
  isRTF: boolean;
  title: string;
  acceptText: string;
  rejectText: string;
  errorMessage: string;
};
export type LicenceNotAcceptedFn = (responseBody: LicenceResponse) => void;
let onLicenceNotAccepted: LicenceNotAcceptedFn = () => {};

export function registerOnLicenceNotAccepted(handler: LicenceNotAcceptedFn) {
  onLicenceNotAccepted = handler;
}

function sessionTokenKey() {
  return 'session-token';
}

// Reference Snapshot Number - used by server to keep reference data consistent for us.
//    We need to always send back the most recent snapshot number we've seen.
function referenceSnapshotNumberKey() {
  return 'reference-snapshot-number';
}

function referenceSnapshotNumber(): bigint {
  return BigInt(SBStorage.getItem(referenceSnapshotNumberKey()) || 0);
}

const jsonContentType = 'application/json';

export async function callAPI(
  endpoint: string,
  method: HTTPMethod,
  params?: APIParams,
  resultIsJson = true,
): Promise<APIResult> {
  const { lockNum, seqNumFrom, queryParams, range, body, requestType } =
    params ?? {};

  const queryParamsSuffix = queryParams
    ? Object.keys(queryParams)
        .filter(key => queryParams[key] !== undefined)
        .map(key => `${key}=${encodeURIComponent(queryParams[key])}`)
        .join('&')
    : '';
  const queryParamsStr = queryParamsSuffix ? '?' + queryParamsSuffix : '';
  const base = SB_CONFIG.serverURL || '';

  const url = `${base}${endpoint}${queryParamsStr}`;

  const headers: Record<string, string> = {};
  formatLockNum(headers, lockNum);
  formatRange(headers, range);

  if (body && method === 'GET') {
    // browsers don't allow GET methods with a body.
    // therefore, convert it to a POST with a X-HTTP-METHOD-Override
    headers['X-HTTP-Method-Override'] = 'GET';
    method = 'POST';
  }
  const sessionToken = getSessionToken();

  if (sessionToken) {
    headers['Authorization'] = `session ${sessionToken}`;
  }
  headers['SB-Snapshot'] = referenceSnapshotNumber().toString();
  if (seqNumFrom) {
    headers['SB-Seq-From'] = seqNumFrom.toString();
  }

  const options = {
    method,
    body: requestType === 'form' ? body : JSON.stringify(body),
    headers,
  };
  const response = await fetch(url, options);

  let json = undefined;

  if (response.body !== null) {
    if (
      (response.headers.get('content-type') ??
        (resultIsJson && jsonContentType)) === jsonContentType
    ) {
      try {
        json = await response.json();
      } catch {
        // Ignore any exceptions while parsing json,
        // It means that the response has been tampered with by an
        //    intermediary, e.g. IIS, to replace the body
        //    with non-JSON (e.g. HTML).
        json = undefined;
      }
    } else {
      const blob = await response.blob();
      json = { blob };
    }
  }

  if (response.status === ResponseCodes.sbUnauthorized) {
    if (sessionToken) {
      SBStorage.removeItem(sessionTokenKey());
      SBStorage.removeItem(referenceSnapshotNumberKey());
    }
    onAuthenticationExpired();

    throw {
      code: response.status,
      errorMessage: json?.errorMessage,
    };
  }

  if (response.status === ResponseCodes.sbLicenceNotAccepted) {
    onLicenceNotAccepted(json);
  }

  const result = {
    response,

    lockNum: parseLockNum(response),
    seqNumCur: parseSeqNumCur(response),
    seqNumPersisted: parseSeqNumPersisted(response),

    modified: json?.result?.modified,
    existsInDatabase: json?.result?.existsInDatabase,

    ...json,
  };

  if (!response.ok) {
    throw {
      code: response.status,
      errorMessage: response.statusText,
      response: result,
      ...json,
    };
  }

  const newSnapshotNum = parseSnapshotNumber(response);
  if (newSnapshotNum) {
    const curSnapshotNum = referenceSnapshotNumber(); // get it again, another request might've updated it since last call
    if (newSnapshotNum > curSnapshotNum) {
      SBStorage.setItem(
        referenceSnapshotNumberKey(),
        newSnapshotNum.toString(),
      );
    } else if (newSnapshotNum < curSnapshotNum) {
      // We've switched between DLLs recently (in between when this request was sent and the response was received),
      //  this response is stale, TODO SM need to throw it away and re-issue the request, or handle it some other way
      // This could potentially result in an infinite chain of callAPIs if server incorrectly returns the wrong RNS#
      //    Need a limit on how many re-tries before just throwing exception
      // return await callAPI(endpoint, method, params);
      // TODO SM This could also happen when updating reference data (e.g. PATCH /glass-types)? To check
    }
  }

  // some apps bake in session config as part of the response, e.g. viewer-apps
  json?.result?.session && updateSessionConfig(json!.result.session);

  if (method === 'PATCH') {
    sentAnyPatch = true;
  }

  return result;
}

export type SSOBody = {
  type: string | null;
  user: string | null;
  token: string | null;
  account: string | null;
};

export function extractSSOParams() {
  const ssoParamNames = ['sso', 'uid', 'token', 'accountId'];

  const currentURL = new URL(window.location.href);
  const ssoParams = new URLSearchParams();
  for (const ssoParamName of ssoParamNames) {
    const ssoParam = currentURL.searchParams.get(ssoParamName);
    if (ssoParam) {
      ssoParams.append(ssoParamName, ssoParam);
    }
    currentURL.searchParams.delete(ssoParamName);
  }

  return { currentURL, ssoParams };
}

export function buildSSOBody(ssoParams: URLSearchParams): SSOBody {
  return {
    type: ssoParams.get('sso'),
    user: ssoParams.get('uid'),
    token: ssoParams.get('token'),
    account: ssoParams.get('accountId'),
  };
}

export function navigateToLoginApp(returnToCurrent: boolean) {
  const { currentURL, ssoParams } = extractSSOParams();

  const next = returnToCurrent
    ? `&next=${encodeURIComponent(currentURL.toString())}`
    : '';
  let target = `${SB_CONFIG.loginURL}?targetXti=${SB_CONFIG.xtiId}${next}`;
  if (ssoParams.size > 0) {
    target = target + '&' + ssoParams.toString();
  }
  window.location.href = target;
}

export async function apiLogin(
  xtiId: number,
  userName?: string,
  password?: string,
  url?: string,
): Promise<APIResult> {
  // clear any existing login
  SBStorage.removeItem(sessionTokenKey(), url);

  const r = await callAPI('/sessions/', 'POST', {
    body: {
      xtiId,
      userName,
      password,
    },
  });

  r.result.token && SBStorage.setItem(sessionTokenKey(), r.result.token, url);
  updateSessionConfig(r.result);

  return r;
}

export async function apiLoginSSO(
  xtiId: number,
  sso: SSOBody,
  url?: string,
): Promise<APIResult> {
  // clear any existing login
  SBStorage.removeItem(sessionTokenKey(), url);

  const r = await callAPI('/sessions/', 'POST', {
    body: {
      xtiId,
      sso,
    },
  });

  r.result.token && SBStorage.setItem(sessionTokenKey(), r.result.token, url);
  updateSessionConfig(r.result);

  return r;
}

export function getXtiId() {
  return SB_CONFIG.xtiId ?? queryXtiId();
}

export function getImagePathPrefix(): string {
  // SS 20230110 Used by the plus-design-test app to allow loading scenes
  // without the need for a DLL. Instead just returns an override path
  // for locating material texture images etc
  if (process.env.NODE_ENV === 'development' && SB_CONFIG.bypassDLL) {
    return SB_CONFIG.overrideImagePathPrefix ?? '';
  }

  const xtiId = SB_CONFIG.xtiId;
  if (!xtiId) {
    console.error('attempting to fetch static images without xtiId');
  }
  return `${SB_CONFIG.serverURL}/static/images/${xtiId}/`;
}

export function getBrandingImagePath(imageName?: string): string {
  let result = getImagePathPrefix() + 'branding';
  if (imageName) {
    result = `${result}/${imageName}`;
  }
  return result;
}

export async function apiAcceptLicence() {
  const sessionToken = SBStorage.getItem(sessionTokenKey());
  if (sessionToken) {
    await callAPI(`/sessions/${sessionToken}/acceptLicence`, 'POST', {});
  }
}

export async function apiLogout() {
  const sessionToken = getSessionToken();
  if (sessionToken) {
    SBStorage.removeItem(sessionTokenKey());
    SBStorage.removeItem(referenceSnapshotNumberKey());
    await callAPI(`/sessions/${sessionToken}/`, 'DELETE', {});
  }
}

async function loadUserIfExists(userId?: number) {
  if (userId) {
    const user = await callAPI('/users/current/', 'GET');
    SB_CONFIG.user = user.result;
  }
}

export async function ensureAuthenticated(
  expireIfNotFound?: boolean,
): Promise<APIResult> {
  const sessionToken = getSessionToken();
  if (sessionToken) {
    const r = await callAPI(`/sessions/${sessionToken}/`, 'GET', {});
    r.result && updateSessionConfig(r.result);
    await loadUserIfExists(r.result?.userId);
    return r;
  } else if (expireIfNotFound) {
    return onAuthenticationExpired();
  } else {
    const r = await apiLogin(SB_CONFIG.xtiId);
    await loadUserIfExists(r.result?.userId);
    return r;
  }
}

export function downloadBlob(source: any, type: string, filename: string) {
  const url = URL.createObjectURL(new Blob([source], { type }));

  // see https://stackoverflow.com/a/60378502
  const a = document.createElement('a');
  a.className = 'dummy-download-blob';
  a.href = url;
  a.download = filename;
  a.target = '_blank'; // don't replace current tab contents (in case browser force-redirects downloads)
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  URL.revokeObjectURL(url);
}

export function blobResponseToDownloadable(result: APIResult): boolean {
  if (!result.blob) {
    return false;
  }

  let filename = '';
  const contentDisposition = result.response.headers.get('Content-Disposition');
  // See https://stackoverflow.com/a/40940790
  // CB 20230901 We can't follow the stackoverflow answer exactly; as some comments point out it does not handle the
  // filename* parameter correctly.
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/gi;
  let matches = filenameRegex.exec(contentDisposition || '');
  // CB 20230901 Both filename= and filename*= could exist for compatibility but prefer filename* because it can handle
  // unicode characters.
  if (matches?.[0]?.toLowerCase()?.startsWith('filename=')) {
    const nextMatches = filenameRegex.exec(contentDisposition || '');
    if (nextMatches?.[0]?.toLowerCase()?.startsWith('filename*')) {
      matches = nextMatches;
    }
  }

  if (matches?.[1]) {
    filename = matches[1];
    if (filename.startsWith('"') && filename.endsWith('"')) {
      // Both filename parameters support this simple case of being surrounded by double quotes.
      filename = filename.substring(1, filename.length - 1);
    } else if (matches?.[0]?.toLowerCase()?.startsWith('filename*')) {
      // Handle possible encoding parameters for the extended parameter. Format is encoding'language'filename, but
      // only filename is required. See: https://datatracker.ietf.org/doc/html/rfc5987
      const filenameExtendedRegex = /^([^']*)'([^']*)'(\S*)$/;
      const extendedMatches = filenameExtendedRegex.exec(filename);
      if (extendedMatches?.[3]) {
        const encoding = extendedMatches[1]; // May be empty string
        // const language = matchesExtended[2]; // May be empty string, but we don't make use of it anyway.
        filename = extendedMatches[3];
        if (encoding?.toLowerCase() === 'utf-8') {
          filename = decodeURIComponent(filename);
        } else if (encoding) {
          console.log(
            'blobResponseToDownloadable: unknown encoding type of ' + encoding,
          );
        }
      }
    }
  }

  if (filename === '') {
    filename = 'Report.pdf'; // reasonable default
  }

  // Using octet-stream to force Firefox to stop replacing the current tab with the pdf.
  // see https://stackoverflow.com/a/11894771
  downloadBlob(result.blob, 'application/octet-stream', filename);

  return true;
}

export function callApiReport(
  url: string,
  imageURI: string,
  resultIsJson?: boolean,
): Promise<APIResult> {
  return callAPI(
    url,
    'GET',
    {
      body: {
        reportImages: {
          group_0: imageURI, // TODO SM Hardcoded to group 0 for now, don't support branching yet
        },
      },
    },
    !!resultIsJson,
  );
}
